import { Controller } from "@hotwired/stimulus"

/**
 * Powers category → subcategory visibility and vehicle make → model visibility.
 * Targets/props used by the filters and form UIs.
 */
export default class extends Controller {
  static targets = [
    // category/subcategory
    "categoryCheckbox",
    "subcategoryGroup",
    // vehicle make/model
    "vehicleMakeCheckbox",
    "vehicleModelGroup",
    // selection fields
    "selectedCategoryField",
    "selectedMakeField"
  ]

  static values = {
    singleCategory: { type: Boolean, default: false },
    singleMake: { type: Boolean, default: false }
  }

  connect() {
    this.syncSubcategoryVisibility()
    this.syncModelVisibility()
    this.syncSelectedCategoryFields()
    this.syncSelectedMakeFields()
  }

  // Exposed so other controllers can force a re-sync (e.g., after Reset)
  syncAll() {
    this.syncSubcategoryVisibility()
    this.syncModelVisibility()
    this.syncSelectedCategoryFields()
    this.syncSelectedMakeFields()
  }

  // ===== Categories → Subcategories ========================================
  onCategoryToggled(event) {
    const checkbox = event.currentTarget
    const catId = this.int(checkbox.dataset.categoryId)
    if (checkbox.checked && this.singleCategoryValue) this.uncheckOtherCategories(checkbox)
    this.toggleSubcategoryGroup(catId, checkbox.checked)
    if (!checkbox.checked) this.uncheckSubcategoriesForCategory(catId)
    if (this.singleCategoryValue) this.syncSubcategoryVisibility()
    this.syncSelectedCategoryFields()
  }

  syncSubcategoryVisibility() {
    const checkedCatIds = new Set(
      this.categoryCheckboxTargets
        .filter(cb => cb.checked && !cb.disabled)
        .map(cb => this.int(cb.dataset.categoryId))
    )
    this.subcategoryGroupTargets.forEach(group => {
      const catId = this.int(group.dataset.categoryId)
      const visible = checkedCatIds.has(catId)
      group.classList.toggle("hidden", !visible)
      if (!visible) this.uncheckChildrenCheckboxes(group)
    })
  }

  toggleSubcategoryGroup(catId, show) {
    this.subcategoryGroupTargets
      .filter(g => this.int(g.dataset.categoryId) === catId)
      .forEach(g => {
        g.classList.toggle("hidden", !show)
        if (!show) this.uncheckChildrenCheckboxes(g)
      })
  }

  uncheckSubcategoriesForCategory(catId) {
    this.subcategoryGroupTargets
      .filter(g => this.int(g.dataset.categoryId) === catId)
      .forEach(g => this.uncheckChildrenCheckboxes(g))
  }

  // ===== Vehicle Makes → Models ============================================
  onMakeToggled(event) {
    const checkbox = event.currentTarget
    const makeId = this.int(checkbox.dataset.makeId)
    if (checkbox.checked && this.singleMakeValue) this.uncheckOtherMakes(checkbox)
    this.toggleModelGroup(makeId, checkbox.checked)
    if (!checkbox.checked) this.uncheckModelsForMake(makeId)
    if (this.singleMakeValue) this.syncModelVisibility()
    this.syncSelectedMakeFields()
  }

  syncModelVisibility() {
    const checkedMakeIds = new Set(
      this.vehicleMakeCheckboxTargets
        .filter(cb => cb.checked && !cb.disabled)
        .map(cb => this.int(cb.dataset.makeId))
    )
    this.vehicleModelGroupTargets.forEach(group => {
      const makeId = this.int(group.dataset.makeId)
      const visible = checkedMakeIds.has(makeId)
      group.classList.toggle("hidden", !visible)
      if (!visible) this.uncheckChildrenCheckboxes(group)
    })
  }

  toggleModelGroup(makeId, show) {
    this.vehicleModelGroupTargets
      .filter(g => this.int(g.dataset.makeId) === makeId)
      .forEach(g => {
        g.classList.toggle("hidden", !show)
        if (!show) this.uncheckChildrenCheckboxes(g)
      })
  }

  uncheckModelsForMake(makeId) {
    this.vehicleModelGroupTargets
      .filter(g => this.int(g.dataset.makeId) === makeId)
      .forEach(g => this.uncheckChildrenCheckboxes(g))
  }

  // ===== Shared helpers =====================================================
  uncheckChildrenCheckboxes(container) {
    container.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(cb => { cb.checked = false })
  }

  uncheckOtherCategories(activeCheckbox) {
    this.categoryCheckboxTargets.forEach(cb => {
      if (cb !== activeCheckbox) cb.checked = false
    })
  }

  uncheckOtherMakes(activeCheckbox) {
    this.vehicleMakeCheckboxTargets.forEach(cb => {
      if (cb !== activeCheckbox) cb.checked = false
    })
  }

  syncSelectedCategoryFields() {
    const selected = this.categoryCheckboxTargets.find(cb => cb.checked)
    const value = selected ? selected.dataset.categoryId || "" : ""
    this.selectedCategoryFieldTargets.forEach(field => { field.value = value })
  }

  syncSelectedMakeFields() {
    const selected = this.vehicleMakeCheckboxTargets.find(cb => cb.checked)
    const value = selected ? selected.dataset.makeId || "" : ""
    this.selectedMakeFieldTargets.forEach(field => { field.value = value })
  }

  int(val) {
    const n = parseInt(val, 10)
    return Number.isNaN(n) ? null : n
  }
}
